/**
 * Custom React Hooks
 * Reusable hooks for common UI patterns
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import i18n from '../i18n';

// ============ useClickOutside ============

/**
 * Hook to detect clicks outside of a referenced element
 * Useful for closing dropdowns, modals, and popovers
 *
 * @param {Function} callback - Function to call when click outside occurs
 * @returns {React.RefObject} - Ref to attach to the element to monitor
 *
 * @example
 * const dropdownRef = useClickOutside(() => setIsOpen(false));
 * return <div ref={dropdownRef}>...</div>
 */
export const useClickOutside = (callback) => {
  const ref = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (ref.current && !ref.current.contains(event.target)) {
        callback(event);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('touchstart', handleClickOutside);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [callback]);

  return ref;
};

// ============ useModal ============

/**
 * Hook for managing modal state
 * Handles open/close state and optional data to pass to modal
 *
 * @param {boolean} initialOpen - Initial open state (default: false)
 * @returns {Object} Modal state and control functions
 *
 * @example
 * const { isOpen, data, open, close, toggle } = useModal();
 * <button onClick={() => open({ id: 1 })}>Edit Item</button>
 * {isOpen && <Modal data={data} onClose={close} />}
 */
export const useModal = (initialOpen = false) => {
  const [isOpen, setIsOpen] = useState(initialOpen);
  const [data, setData] = useState(null);

  const open = useCallback((modalData = null) => {
    setData(modalData);
    setIsOpen(true);
  }, []);

  const close = useCallback(() => {
    setIsOpen(false);
    setData(null);
  }, []);

  const toggle = useCallback((modalData = null) => {
    if (isOpen) {
      close();
    } else {
      open(modalData);
    }
  }, [isOpen, open, close]);

  return {
    isOpen,
    data,
    open,
    close,
    toggle,
    setData
  };
};

// ============ useAsyncState ============

/**
 * Hook for managing async operation state (loading, error, data)
 * Simplifies handling of API calls and async operations
 *
 * @param {Function} asyncFn - Async function to execute
 * @param {boolean} executeOnMount - Whether to execute on mount (default: false)
 * @param {Array} deps - Dependencies for re-execution
 * @returns {Object} State and control functions
 *
 * @example
 * const { data, loading, error, execute, reset } = useAsyncState(fetchUserData);
 *
 * useEffect(() => { execute(userId); }, [userId]);
 *
 * if (loading) return <Spinner />;
 * if (error) return <Error message={error} />;
 * return <UserProfile data={data} />;
 */
export const useAsyncState = (asyncFn, executeOnMount = false, deps = []) => {
  const [state, setState] = useState({
    data: null,
    loading: false,
    error: null
  });

  const execute = useCallback(async (...args) => {
    setState(prev => ({ ...prev, loading: true, error: null }));

    try {
      const result = await asyncFn(...args);
      setState({ data: result, loading: false, error: null });
      return result;
    } catch (error) {
      const errorMessage = error.message || i18n.t('errors.generic', 'An error occurred');
      setState(prev => ({ ...prev, loading: false, error: errorMessage }));
      throw error;
    }
  }, [asyncFn]);

  const reset = useCallback(() => {
    setState({ data: null, loading: false, error: null });
  }, []);

  const setData = useCallback((data) => {
    setState(prev => ({ ...prev, data }));
  }, []);

  useEffect(() => {
    if (executeOnMount) {
      execute();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  return {
    ...state,
    execute,
    reset,
    setData,
    isIdle: !state.loading && !state.error && !state.data,
    isSuccess: !state.loading && !state.error && state.data !== null,
    isError: !state.loading && state.error !== null
  };
};

// ============ useDebounce ============

/**
 * Hook to debounce a value
 * Useful for search inputs and other frequent updates
 *
 * @param {any} value - Value to debounce
 * @param {number} delay - Debounce delay in milliseconds
 * @returns {any} Debounced value
 *
 * @example
 * const [searchTerm, setSearchTerm] = useState('');
 * const debouncedSearch = useDebounce(searchTerm, 300);
 *
 * useEffect(() => {
 *   if (debouncedSearch) fetchResults(debouncedSearch);
 * }, [debouncedSearch]);
 */
export const useDebounce = (value, delay) => {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(timer);
    };
  }, [value, delay]);

  return debouncedValue;
};

// ============ useLocalStorage ============

/**
 * Hook to sync state with localStorage
 * Persists state across page reloads
 *
 * @param {string} key - localStorage key
 * @param {any} initialValue - Initial value if key doesn't exist
 * @returns {Array} [value, setValue] - Similar to useState
 *
 * @example
 * const [theme, setTheme] = useLocalStorage('theme', 'dark');
 */
export const useLocalStorage = (key, initialValue) => {
  const [storedValue, setStoredValue] = useState(() => {
    try {
      const item = window.localStorage.getItem(key);
      return item ? JSON.parse(item) : initialValue;
    } catch (error) {
      console.error(`Error reading localStorage key "${key}":`, error);
      return initialValue;
    }
  });

  const setValue = useCallback((value) => {
    try {
      const valueToStore = value instanceof Function ? value(storedValue) : value;
      setStoredValue(valueToStore);
      window.localStorage.setItem(key, JSON.stringify(valueToStore));
    } catch (error) {
      console.error(`Error setting localStorage key "${key}":`, error);
    }
  }, [key, storedValue]);

  return [storedValue, setValue];
};

// ============ usePrevious ============

/**
 * Hook to get the previous value of a state/prop
 * Useful for comparing current and previous values
 *
 * @param {any} value - Current value
 * @returns {any} Previous value
 *
 * @example
 * const [count, setCount] = useState(0);
 * const prevCount = usePrevious(count);
 * // prevCount will be undefined on first render, then the previous count value
 */
export const usePrevious = (value) => {
  const ref = useRef();

  useEffect(() => {
    ref.current = value;
  }, [value]);

  return ref.current;
};

// ============ useFocusTrap ============

/**
 * Hook to trap focus within a modal/dialog for accessibility
 * Handles keyboard navigation (Tab, Shift+Tab, Escape)
 * Returns focus to trigger element when modal closes
 *
 * @param {boolean} isActive - Whether the focus trap is active
 * @param {Object} options - Configuration options
 * @param {Function} options.onEscape - Callback when Escape is pressed
 * @param {boolean} options.autoFocus - Auto-focus first element (default: true)
 * @param {boolean} options.restoreFocus - Restore focus on close (default: true)
 * @returns {React.RefObject} - Ref to attach to the container element
 *
 * @example
 * const modalRef = useFocusTrap(isOpen, { onEscape: closeModal });
 * return <div ref={modalRef} role="dialog">...</div>
 */
export const useFocusTrap = (isActive, options = {}) => {
  const {
    onEscape = null,
    autoFocus = true,
    restoreFocus = true
  } = options;

  const containerRef = useRef(null);
  const previousActiveElement = useRef(null);

  // Get all focusable elements within the container
  const getFocusableElements = useCallback(() => {
    if (!containerRef.current) return [];

    const focusableSelectors = [
      'a[href]',
      'button:not([disabled])',
      'textarea:not([disabled])',
      'input:not([disabled])',
      'select:not([disabled])',
      '[tabindex]:not([tabindex="-1"])',
      '[contenteditable="true"]'
    ].join(', ');

    return Array.from(containerRef.current.querySelectorAll(focusableSelectors))
      .filter(el => {
        // Exclude hidden elements
        return el.offsetParent !== null && getComputedStyle(el).visibility !== 'hidden';
      });
  }, []);

  // Handle keyboard events
  useEffect(() => {
    if (!isActive) return;

    const handleKeyDown = (event) => {
      // Handle Escape key
      if (event.key === 'Escape' && onEscape) {
        event.preventDefault();
        onEscape();
        return;
      }

      // Handle Tab key for focus trapping
      if (event.key === 'Tab') {
        const focusableElements = getFocusableElements();
        if (focusableElements.length === 0) return;

        const firstElement = focusableElements[0];
        const lastElement = focusableElements[focusableElements.length - 1];

        // Shift + Tab (going backward)
        if (event.shiftKey) {
          if (document.activeElement === firstElement) {
            event.preventDefault();
            lastElement.focus();
          }
        }
        // Tab (going forward)
        else {
          if (document.activeElement === lastElement) {
            event.preventDefault();
            firstElement.focus();
          }
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isActive, onEscape, getFocusableElements]);

  // Handle focus on activation/deactivation
  useEffect(() => {
    if (isActive) {
      // Store the currently focused element
      if (restoreFocus) {
        previousActiveElement.current = document.activeElement;
      }

      // Auto-focus the first focusable element
      if (autoFocus) {
        // Use setTimeout to ensure the modal is rendered
        const timer = setTimeout(() => {
          const focusableElements = getFocusableElements();
          if (focusableElements.length > 0) {
            focusableElements[0].focus();
          } else if (containerRef.current) {
            // If no focusable elements, focus the container itself
            containerRef.current.focus();
          }
        }, 0);

        return () => clearTimeout(timer);
      }
    } else {
      // Restore focus to the previous element
      if (restoreFocus && previousActiveElement.current) {
        previousActiveElement.current.focus();
        previousActiveElement.current = null;
      }
    }
  }, [isActive, autoFocus, restoreFocus, getFocusableElements]);

  // Prevent focus from leaving the container
  useEffect(() => {
    if (!isActive) return;

    const handleFocusIn = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        event.stopPropagation();
        const focusableElements = getFocusableElements();
        if (focusableElements.length > 0) {
          focusableElements[0].focus();
        }
      }
    };

    document.addEventListener('focusin', handleFocusIn);

    return () => {
      document.removeEventListener('focusin', handleFocusIn);
    };
  }, [isActive, getFocusableElements]);

  return containerRef;
};
